import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DateTime } from 'luxon';
import { FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import {
  useFormattedDuration,
  FormErrorDialogContextProvider
} from 'modules/notes/common';
import {
  TimesheetLabels,
  LabelsFormFieldContextProvider
} from 'modules/timesheet-labels';
import {
  useIsBreakpointDown,
  useLabels,
  useDialogCancelAction,
  useFeatureFlag
} from 'modules/common/hooks';
import { TimeSignal, TimeSignalClassification } from 'graphql/types';
import { FormTimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { useDialogStyles } from 'modules/notes/addNote/AddNoteDialog';
import { useTimeSignalAttributes } from 'modules/notes/addNote/hooks';
import { NOTE_FIELDS } from 'modules/notes/common/noteSchema';
import { useTotalDuration } from 'modules/App/hooks/useTotalDuration';
import { NoteContextProvider } from 'modules/notes/NoteContext';
import { NoteDialogFooter } from 'modules/common/components';
import { useScrollbarStyles } from 'modules/common/styles';
import { CustomLabels } from 'modules/customLabels/components/CustomLabels';
import { NoteListItem } from 'modules/notes/list';
import { delay } from 'utils/delay';
import { useNotesFilterContext } from '../NotesFilterContextProvider';
import { useMultiEditForm } from './hooks';
import { useMultiEditBulkActionCallback } from './hooks';

export const messages = {
  cancelDialogTitle: <FormattedMessage id="note.multiEdit.cancelTitle" />,
  cancelDialogBodyMessages: [
    <FormattedMessage
      key="note.multiEdit.cancelMessage1"
      id="note.multiEdit.cancelMessage1"
    />,
    <FormattedMessage
      key="note.multiEdit.cancelMessage2"
      id="note.multiEdit.cancelMessage2"
    />
  ]
};

const useStyles = makeStyles((theme) => ({
  title: {
    ...theme.typography.h6,
    padding: theme.spacing(2, 2, 0, 2),
    color: theme.palette.grey[700],
    display: 'flex',
    justifyContent: 'space-between'
  },
  content: {
    padding: 0
  },
  footer: {
    padding: theme.spacing(1, 2.5)
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginRight: theme.spacing(1)
  },
  headerText: {
    fontWeight: 400
  },
  totalDuration: {
    fontWeight: 600
  },
  divider: {
    backgroundColor: theme.palette.grey[100],
    marginTop: theme.spacing(0.5)
  },
  notesInfoList: {
    maxHeight: '500px',
    overflowY: 'auto',
    padding: theme.spacing(0, 2),
    backgroundColor: theme.palette.table?.header,
    marginTop: theme.spacing(1)
  },
  timesheetLabelsContainer: {
    marginTop: 0
  },
  loading: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    padding: theme.spacing(1, 0)
  }
}));

type MultiEditDialogProps = {
  open: boolean;
  onClose: () => void;
  timeSignals: TimeSignal[];
};

export const SelectedNotesText = ({
  totalCount
}: {
  totalCount: number;
}): React.ReactElement => {
  const values = useMemo(() => ({ totalCount }), [totalCount]);

  if (totalCount === 1) {
    return <FormattedMessage id="noteSelector.oneSelected" />;
  }

  return <FormattedMessage id="noteSelector.notesSelected" values={values} />;
};

const PAGE_SIZE = 15;

export const MultiEditDialog: React.FC<MultiEditDialogProps> = ({
  open,
  onClose,
  timeSignals
}: MultiEditDialogProps) => {
  const classes = useStyles();
  const classesScrollbar = useScrollbarStyles();

  const timeSignal = timeSignals[0];
  const noteDate = useMemo(() => DateTime.fromISO(timeSignal?.date), [
    timeSignal?.date
  ]);

  const { timesheetLabels, loading: labelLoading } = useLabels(noteDate, true);
  const {
    formMethods,
    isFormDirty,
    isValidForm,
    timesheetLabelFields
  } = useMultiEditForm(timesheetLabels);

  const cancelActionProps = useDialogCancelAction({
    onClose,
    isFormDirty,
    isValidForm
  });
  const { onNoteDiscard } = cancelActionProps;
  const fullScreen = useIsBreakpointDown('xs');

  const { attributes } = useTimeSignalAttributes(timeSignal?.id);

  const totalDuration = useTotalDuration(timeSignals);

  const formattedTotalDuration = useFormattedDuration({
    needsMask: true,
    duration: totalDuration ?? 0
  });

  const timesheetLabelClasses = useMemo(
    () => ({ container: classes.timesheetLabelsContainer }),
    [classes.timesheetLabelsContainer]
  );

  const {
    bulkActionCallback,
    isActionInProgress
  } = useMultiEditBulkActionCallback({
    notes: timeSignals,
    getValues: formMethods.getValues
  });

  const {
    filterContext: { selectedClassification }
  } = useNotesFilterContext();

  const classification = useMemo(
    () =>
      selectedClassification !== 'All'
        ? TimeSignalClassification[selectedClassification]
        : undefined,
    [selectedClassification]
  );

  // pagination logic to improve rendering time
  const isBulkEditPerformanceEnabled = useFeatureFlag(
    'isBulkEditPerformanceEnabled'
  );
  const currentPage = useRef(0);
  const [loading, setLoading] = useState(false);
  const [paginatedSignals, setPaginatedSignals] = useState<TimeSignal[]>(
    timeSignals.slice(0, isBulkEditPerformanceEnabled ? PAGE_SIZE : undefined)
  );
  const hasNextPage = useMemo(
    () => timeSignals.length > paginatedSignals.length,
    [paginatedSignals.length, timeSignals.length]
  );

  const loadMore = useCallback(async () => {
    setLoading(true);
    currentPage.current += 1;
    setPaginatedSignals((signals) => {
      return [
        ...signals,
        ...timeSignals.slice(
          PAGE_SIZE * currentPage.current,
          PAGE_SIZE * currentPage.current + PAGE_SIZE
        )
      ];
    });
    await delay(100);
    setLoading(false);
  }, [timeSignals]);

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 50px 0px'
  });

  return (
    <FormProvider {...formMethods}>
      <FormErrorDialogContextProvider>
        <NoteContextProvider date={noteDate}>
          <Dialog
            data-testid="multi-edit-note-dialog"
            classes={useDialogStyles()}
            open={open}
            onClose={onNoteDiscard}
            fullScreen={fullScreen}
            disableEnforceFocus
          >
            <DialogTitle
              className={classes.title}
              data-testid="multi-edit-note-dialog-title"
            >
              <FormattedMessage id="note.multiEdit.title" />
              <div
                className={classes.header}
                data-testid="multi-edit-notes-selected-count"
              >
                <Typography
                  className={classes.headerText}
                  variant={'caption'}
                  color={'textSecondary'}
                >
                  <SelectedNotesText totalCount={timeSignals.length} />
                </Typography>
                <Typography
                  className={classes.headerText}
                  variant={'caption'}
                  color={'textSecondary'}
                >
                  &nbsp; - &nbsp;
                </Typography>
                <Typography
                  className={classes.headerText}
                  variant={'caption'}
                  color={'textSecondary'}
                >
                  <FormattedMessage id="note.total" /> : &nbsp;
                </Typography>
                <Typography
                  className={classes.totalDuration}
                  variant={'caption'}
                  color={'textSecondary'}
                  data-testid="multi-edit-total-time-duration"
                >
                  {formattedTotalDuration}
                </Typography>
              </div>
            </DialogTitle>

            <DialogContent
              className={classnames(
                classes.content,
                classesScrollbar.scrollbar
              )}
              data-testid="multi-edit-note-dialog-content"
            >
              <div
                className={classnames(
                  classes.notesInfoList,
                  classesScrollbar.scrollbar
                )}
                ref={rootRef}
              >
                {paginatedSignals.map((timeSignal) => (
                  <NoteListItem
                    key={timeSignal.id}
                    note={timeSignal}
                    readOnly
                  />
                ))}

                {isBulkEditPerformanceEnabled && (loading || hasNextPage) && (
                  <ListItem
                    ref={sentryRef}
                    className={classes.loading}
                    data-testid="multi-edit-inifinite-scroll-loading"
                  >
                    <CircularProgress size={18} />
                    <Typography variant="body2">
                      <FormattedMessage id="smartDiary.loading" />
                    </Typography>
                  </ListItem>
                )}
              </div>

              <LabelsFormFieldContextProvider
                timesheetLabels={NOTE_FIELDS.TIMESHEET_LABELS}
                note={NOTE_FIELDS.NOTE}
              >
                <CustomLabels fieldName={NOTE_FIELDS.CUSTOM_LABELS} />
                <TimesheetLabels
                  loading={labelLoading}
                  fields={timesheetLabelFields as FormTimesheetLabel[]}
                  attributes={attributes}
                  classes={timesheetLabelClasses}
                />
              </LabelsFormFieldContextProvider>
            </DialogContent>

            <DialogActions
              className={classes.footer}
              data-testid="multi-edit-note-dialog-footer"
            >
              <NoteDialogFooter
                {...cancelActionProps}
                {...messages}
                onSave={bulkActionCallback}
                isActionInProgress={isActionInProgress}
                selectedClassification={classification}
              />
            </DialogActions>
          </Dialog>
        </NoteContextProvider>
      </FormErrorDialogContextProvider>
    </FormProvider>
  );
};
